import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { isLoading, isErrored, LoadingInstance, ErroredInstance } from 'rwwa-data-access'
import { ButtonBlock } from '@mobi/component-library/Common/Buttons'
import { PageHeader } from '@mobi/component-library/Navigation/PageHeader'
import { Spinner, DesktopSpinner } from '@mobi/component-library/Common/Spinner'
import { useBiometricsStatus } from '@mobi/account/Areas/Verification/Biometrics/Hooks/useBiometricsStatus'
import { connect } from '@core/Components/HOCs/connect'
import { observeImmutable } from '@core/Components/HOCs'
import { ErrorMessage, SuccessMessage } from '@core/Components/Messages'
import { MainContainer } from '@core/Components/Containers'
import {
  getIsBiometricsEnabledOnTempPasswordChange,
  getIsKeepMeLoggedIn,
} from '@core/Areas/Login/Store/selectors'
import {
  ChangePasswordRepo,
  ChangePasswordKey,
  AccountDetails,
  MfaChallenge,
} from '@core/Data/Account/changePassword'
import { ComplexPasswordObserve } from '../Password'
import {
  ChangePasswordTitleLabelStyled,
  ChangePasswordContainerStyled,
} from './ChangePassword.styles'
import { CurrentPassword } from './Components/CurrentPassword'
import {
  state$ as changePasswordState$,
  ChangePasswordSubmit,
  ChangePasswordStateRecord,
  UpdateCurrentPassword,
  Reset,
} from './driver'
import { ChangePasswordVerificationCodePageObserve } from './ChangePasswordVerificationCodePage'

export const ChangePassword = ({ isDesktop }: { isDesktop?: boolean }) => {
  return <ConnectChangePassword changePassword={ChangePasswordKey} isDesktop={isDesktop} />
}

export const ChangePasswordLoader = ({
  changePassword,
  isDesktop,
}: {
  changePassword: typeof LoadingInstance | typeof ErroredInstance | AccountDetails
  isDesktop?: boolean
}) => {
  if (isLoading(changePassword)) {
    return isDesktop ? <DesktopSpinner /> : <Spinner />
  }

  if (isErrored(changePassword)) {
    return <ErrorMessage>An error occurred while loading change password.</ErrorMessage>
  }

  const ChangePasswordComponent = () => {
    React.useEffect(() => {
      return () => Reset()
    })
    const props = { accountNumber: changePassword.accountNumber as string }
    return <ChangePasswordViewObserve {...props} isDesktop={isDesktop} />
  }

  return <ChangePasswordComponent />
}

export const ConnectChangePassword = connect({ changePassword: ChangePasswordRepo })<{
  isDesktop?: boolean
}>(ChangePasswordLoader)

interface ChangePasswordViewProps {
  isDesktop?: boolean
  isChangeTemporaryPassword?: boolean
  accountNumber: string
  isChangePasswordSuccess: boolean
  isChangePasswordSubmitEnabled: boolean
  validationError: string | false
}

export const ChangePasswordViewObserve = observeImmutable<
  ChangePasswordStateRecord,
  { accountNumber: string; isDesktop?: boolean; isChangeTemporaryPassword?: boolean }
>(changePasswordState$, ({ record, ...props }) => {
  const { isChangePasswordSuccess, isChangePasswordSubmitEnabled, validationError } = record

  return (
    <ChangePasswordView
      {...props}
      isChangePasswordSuccess={isChangePasswordSuccess}
      isChangePasswordSubmitEnabled={isChangePasswordSubmitEnabled}
      validationError={validationError}
    />
  )
})

let changePasswordForm: HTMLFormElement

export const ChangePasswordView = ({
  isDesktop,
  accountNumber,
  isChangeTemporaryPassword,
  isChangePasswordSuccess,
  isChangePasswordSubmitEnabled,
  validationError,
}: ChangePasswordViewProps) => {
  const { isBiometricsEnabled } = useBiometricsStatus()
  const isBiometricsEnabledOnTempPasswordChange = useSelector(
    getIsBiometricsEnabledOnTempPasswordChange
  )
  const isKeepMeLoggedIn = useSelector(getIsKeepMeLoggedIn)
  const [mfaChallenge, setMfaChallenge] = useState<MfaChallenge>()

  const onChangeCurrent = (event: React.FormEvent<HTMLInputElement>) => {
    UpdateCurrentPassword(event.currentTarget.value)
  }
  const changePasswordSubmit = () =>
    ChangePasswordSubmit({
      accountNumber,
      form: changePasswordForm,
      isBiometricsEnabledByUser: isBiometricsEnabled || isBiometricsEnabledOnTempPasswordChange,
      isDesktop,
      isChangeTemporaryPassword,
      onMfaChallengeReceived: setMfaChallenge,
    })
  const onFormClick = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
  }

  if (mfaChallenge && !isDesktop) {
    return (
      <MainContainer data-tid-changepassword-mfa-container=''>
        <ChangePasswordVerificationCodePageObserve
          accountNumber={accountNumber}
          mfaChallenge={mfaChallenge}
          isBiometricsEnabled={isBiometricsEnabled || isBiometricsEnabledOnTempPasswordChange}
          isChangeTemporaryPassword={isChangeTemporaryPassword}
          isKeepMeLoggedIn={isKeepMeLoggedIn}
          form={changePasswordForm}
          onMfaChallengeReceived={setMfaChallenge}
          onMfaSuccessful={() => setMfaChallenge(undefined)}
        />
      </MainContainer>
    )
  }

  return (
    <MainContainer data-tid-changepassword-main-container=''>
      {!isDesktop && <PageHeader title='Change Password' />}

      {isChangePasswordSuccess ? (
        <SuccessMessage>Successfully changed password</SuccessMessage>
      ) : null}
      {!isChangePasswordSuccess && validationError ? (
        <ErrorMessage>{validationError}</ErrorMessage>
      ) : null}
      <ChangePasswordContainerStyled>
        <ChangePasswordTitleLabelStyled>Account: {accountNumber}</ChangePasswordTitleLabelStyled>
        <form
          ref={(el: HTMLFormElement) => (changePasswordForm = el)}
          onClick={onFormClick}
          id='changePasswordForm'
        >
          <fieldset>
            {!isChangeTemporaryPassword && (
              <CurrentPassword labelName='Current Password*' onChange={onChangeCurrent} />
            )}
            <ComplexPasswordObserve />
          </fieldset>
          <ButtonBlock
            onClick={changePasswordSubmit}
            disabled={!isChangePasswordSubmitEnabled}
            form='changePasswordForm'
          >
            Change Password
          </ButtonBlock>
        </form>
      </ChangePasswordContainerStyled>
    </MainContainer>
  )
}
